import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import { Notification, NotificationBing, CloseCircle } from 'iconsax-react';

interface NotificationPermissionDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => Promise<void>;
  onDeny: () => void;
  loading?: boolean;
}

const NotificationPermissionDialog: React.FC<NotificationPermissionDialogProps> = ({
  open,
  onClose,
  onAccept,
  onDeny,
  loading = false
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    try {
      setError(null);
      await onAccept();
    } catch (err: any) {
      setError(err.message || 'Failed to enable notifications');
    }
  };

  const handleDeny = () => {
    onDeny();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <NotificationBing size={24} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="div">
            Enable Push Notifications
          </Typography>
          <Chip 
            label="Recommended" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseCircle size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body1" gutterBottom>
          Stay updated with real-time notifications for important updates, messages, and system alerts.
        </Typography>

        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Benefits of enabling notifications:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Real-time alerts for urgent tasks and messages
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              System updates and maintenance notifications
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Enhanced collaboration and communication
            </Typography>
            <Typography component="li" variant="body2">
              Never miss important deadlines or events
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> You can change notification preferences anytime in your browser settings or profile settings.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleDeny}
          variant="outlined"
          color="secondary"
          disabled={loading}
        >
          Not Now
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={<Notification size={18} />}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Enabling...' : 'Enable Notifications'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPermissionDialog;
